<script setup lang="ts">
import type {HouseLandPackage} from "./../../graphql/house-land-packages";
import type {HouseLandPackagesCollectionItem} from '@nuxt/content';
import {computed} from "vue";

const props = defineProps<{
  item: HouseLandPackage | HouseLandPackagesCollectionItem,
  popup: string,
}>();

const emit = defineEmits<{
  enquire: [packageData: { name: string, variation: string, suburb: string }]
}>();

const active = ref(0);
const item = props.item as HouseLandPackage;
const variations = item.houseAndLandPackageFields?.variation || [];
const activeVariation = computed(() => variations[active.value]);

const handleEnquire = () => {
  const packageName = item.title || '';
  const variationName = activeVariation.value?.name || '';
  const suburbName = item.houseAndLandPackageFields?.suburb?.nodes[0]?.name || '';

  emit('enquire', {
    name: packageName,
    variation: variationName,
    suburb: suburbName
  });
};
</script>

<template>
  <div class="bg-secondary rounded-5 house-land-packages-grid-item mb-3 mb-md-0">
    <div class="card bg-secondary rounded-4 border-0">
      <div class="card-img-container overflow-hidden rounded-4">
        <a :href="item.uri">
          <nuxt-img
              v-if="item.featuredImage?.node?.mediaItemUrl"
              height="250"
              :srcset="item.featuredImage?.node?.srcSet"
              :src="item.featuredImage?.node?.mediaItemUrl"
              class="card-img-top hover-scale bg-transparent rounded-4 border-0 object-fit-cover"
              :alt="item.featuredImage?.node?.title"/>
          <nuxt-img
              v-else
              height="250"
              src="/MH-Logo2-dark.png"
              class="card-img-top bg-transparent rounded-4 border-0 object-fit-cover p-5 opacity-25"
              :alt="item.title"/>
        </a>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="mb-2 col-12 px-0 d-flex flex-nowrap flex-row justify-content-between align-items-start">
            <a class="text-nowrap fw-bold text-primary"
               :href="item.uri">{{ item.houseAndLandPackageFields?.suburb?.nodes[0]?.name }}</a>
            <span class="text-nowrap d-flex flex-row justify-content-end align-items-center">
              <a v-for="(v,k) in item.houseAndLandPackageFields.variation"
                 @click.prevent="active = k"
                 :class="{
                   'fs-bold': k === active,
                   'text-primary': true,
                   'ms-3': k > 0,
                 }" href="#">{{ v.name }}</a>
            </span>
          </div>
          <div class="col-12 px-0 d-flex flex-nowrap flex-row justify-content-between align-items-center">
            <span class="text-nowrap fw-bold fs-4">${{ new Intl.NumberFormat().format(activeVariation?.price) }}</span>
            <span class="text-nowrap d-flex flex-row justify-content-end align-items-center">
              <span class="small">{{ activeVariation?.bed }} Bed</span>
              <span class="mx-2 fs-5">•</span>
              <span class="small">{{ activeVariation?.bath }} Bath</span>
              <span class="mx-2 fs-5">•</span>
              <span class="small">{{ activeVariation?.car }} Car</span>
              <span class="mx-2 fs-5">•</span>
              <span class="small">{{ activeVariation?.houseSize }} m²</span>
            </span>
          </div>
        </div>
      </div>
      <div class="card-footer border-0 bg-transparent p-0">
        <div class="d-grid gap-3 d-md-flex justify-content-md-between">
          <button class="text-center btn btn-primary text-uppercase rounded-pill px-5 shadow-none"
                  :data-bs-target="props.popup"
                  data-bs-toggle="modal"
                  type="button"
                  @click="handleEnquire">Enquire Now
          </button>
          <a class="text-center btn btn-outline-primary text-uppercase rounded-pill px-4 shadow-none"
             :target="activeVariation?.brochure?.target"
             :href="activeVariation?.brochure?.url">Download Brochure</a>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
.house-land-packages-grid-item {
  padding: 2rem;

  &:hover {
    box-shadow: var(--bs-box-shadow) !important;
  }

  .card-img-container {

    .card-img-top {
      transition: transform 0.3s ease;
    }

    &:hover {
      .hover-scale {
        transform: scale(1.05);
      }
    }
  }

  .card-img-top {
    height: 350px;
  }

  .btn-primary:hover {
    border-color: var(--bs-primary);
  }

  .btn-outline-primary {
    background-color: transparent;
    border-color: var(--bs-primary);
    color: var(--bs-primary);

    &:hover {
      background-color: var(--bs-primary);
      color: var(--bs-secondary);
    }
  }
}
</style>
